<script lang="ts">
	import { appStore } from '../stores/appStore';
	import { getTokenStorage } from '../utils/localStorage';
	import type { AppApiClient } from '../api_contracts/app.client';
	import Preloader from '../components/auth/Preloader.svelte';
	import DarkModeToggle from '../components/auth/DarkModeToggle.svelte';
	import BrandingSide from '../components/auth/BrandingSide.svelte';
	import BackToDashboard from '../components/auth/BackToDashboard.svelte';

	let client: AppApiClient | null = $state(null);
	let email = $state('');
	let loading = $state(false);
	let error = $state('');
	let success = $state(false);

	// Check if user is already logged in
	$effect(() => {
		const token = getTokenStorage();
		if (token) {
			// Redirect to already logged in page
			window.location.hash = '/already-logged-in';
		}
	});

	// Subscribe to store
	appStore.subscribe((state) => {
		client = state.client;
	});

	async function handleForgotPassword(e: Event) {
		e.preventDefault();

		if (!client) {
			error = 'Client not initialized';
			return;
		}

		if (!email) {
			error = 'Please enter your email address';
			return;
		}

		try {
			loading = true;
			error = '';

			const result = await client.forgotPassowrd({ email });

			if (result.response.successFull) {
				success = true;
			} else {
				error = result.response.reason || 'Failed to send reset code';
			}
		} catch (e: any) {
			error = e.message || 'Error sending reset code';
			console.error('Forgot password error:', e);
		} finally {
			loading = false;
		}
	}
</script>

<Preloader />
<DarkModeToggle />

<div class="relative z-1 bg-white p-6 sm:p-0 dark:bg-gray-900">
	<div
		class="relative flex h-screen w-full flex-col justify-center sm:p-0 lg:flex-row dark:bg-gray-900"
	>
		<!-- Left Side: Form -->
		<div class="flex w-full flex-1 flex-col lg:w-1/2">
			<div class="mx-auto w-full max-w-md pt-10">
				<!-- <BackToDashboard /> -->
			</div>

			<div class="mx-auto flex w-full max-w-md flex-1 flex-col justify-center">
				{#if success}
					<!-- Success State -->
					<div class="text-center py-4">
						<svg
							class="w-16 h-16 text-green-500 mx-auto mb-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						<h2 class="text-xl font-semibold text-gray-900 mb-2 dark:text-white/90">
							Check your email!
						</h2>
						<p class="text-sm text-gray-500 mb-6 dark:text-gray-400">
							We've sent a password reset code to <strong>{email}</strong>
						</p>
						<a
							href="#/reset-password"
							class="bg-brand-500 shadow-theme-xs hover:bg-brand-600 inline-block w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-white transition"
						>
							Enter reset code
						</a>
					</div>
				{:else}
					<!-- Form State -->
					<div class="mb-5 sm:mb-8">
						<h1
							class="text-title-sm sm:text-title-md mb-2 font-semibold text-gray-800 dark:text-white/90"
						>
							Forgot Your Password?
						</h1>
						<p class="text-sm text-gray-500 dark:text-gray-400">
							Enter the email address linked to your account, and we'll send you a link to reset
							your password.
						</p>
					</div>

					<div>
						<form onsubmit={handleForgotPassword}>
							{#if error}
								<div
									class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800"
								>
									<p class="text-sm text-red-600 dark:text-red-400">{error}</p>
								</div>
							{/if}

							<div class="space-y-5">
								<!-- Email -->
								<div>
									<label
										for="email"
										class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
									>
										Email<span class="text-error-500">*</span>
									</label>
									<input
										type="email"
										id="email"
										name="email"
										bind:value={email}
										disabled={loading}
										placeholder="Enter your email"
										class="shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
										required
									/>
								</div>

								<!-- Button -->
								<div>
									<button
										type="submit"
										disabled={loading}
										class="bg-brand-500 shadow-theme-xs hover:bg-brand-600 flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-medium text-white transition disabled:bg-gray-400 disabled:cursor-not-allowed"
									>
										{loading ? 'Sending...' : 'Send Reset Link'}
									</button>
								</div>
							</div>
						</form>

						<div class="mt-5">
							<p
								class="text-center text-sm font-normal text-gray-700 sm:text-start dark:text-gray-400"
							>
								Wait, I remember my password...
								<a href="#/login" class="text-brand-500 hover:text-brand-600 dark:text-brand-400"
									>Click here</a
								>
							</p>
						</div>
					</div>
				{/if}
			</div>
		</div>

		<!-- Right Side: Branding -->
		<BrandingSide />
	</div>
</div>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
	}
</style>
